import { useState, useEffect, useMemo } from 'react'
import { fetchEvent } from '@common/features/Events/EventDao'
import { CalendarEvent } from '@common/types/EventsTypes'
import { parseFetchedEvent } from '@common/features/Events/transformers/parseFetchedEvent'

export function useMasterEvent(
  event: CalendarEvent | null | undefined,
  open: boolean,
  typeOfAction: 'solo' | 'all' | undefined
): {
  masterEvent: CalendarEvent | null
  isLoadingMasterEvent: boolean
  effectiveEvent: CalendarEvent | null | undefined
} {
  const [masterEvent, setMasterEvent] = useState<CalendarEvent | null>(null)
  const [isLoadingMasterEvent, setIsLoadingMasterEvent] = useState(false)

  useEffect(() => {
    if (!event || !open || typeOfAction !== 'all') {
      setMasterEvent(null)
      setIsLoadingMasterEvent(false)
      return
    }

    if (!event.repetition?.freq) {
      setMasterEvent(null)
      setIsLoadingMasterEvent(false)
      return
    }

    const [baseUID, recurrenceId] = event.uid.split('/')
    if (!recurrenceId) {
      setMasterEvent(event)
      setIsLoadingMasterEvent(false)
      return
    }

    let cancelled = false

    const fetchMaster = async (): Promise<void> => {
      setIsLoadingMasterEvent(true)
      try {
        const masterEventToFetch = { ...event, uid: baseUID }
        const response = await fetchEvent(masterEventToFetch)
        const fetched = parseFetchedEvent(masterEventToFetch, response, true)

        if (!cancelled) setMasterEvent(fetched)
      } catch (err) {
        console.error('Failed to fetch master event:', err)
        if (!cancelled) setMasterEvent(event)
      } finally {
        setIsLoadingMasterEvent(false)
        if (!cancelled) setIsLoadingMasterEvent(false)
      }
    }

    void fetchMaster()

    return (): void => {
      cancelled = true
    }
  }, [event, open, typeOfAction])

  const effectiveEvent = useMemo(() => {
    const shouldShowMaster =
      typeOfAction === 'all' && !!masterEvent && !isLoadingMasterEvent

    return shouldShowMaster ? masterEvent : event
  }, [typeOfAction, masterEvent, isLoadingMasterEvent, event])

  return { masterEvent, isLoadingMasterEvent, effectiveEvent }
}
